/**
 * TyJolt Dev Wallet Signer
 *
 * Provides a browser-side transaction signer for TyJolt mode.
 * TonConnect doesn't work with TyJolt — this uses pre-funded
 * dev accounts from TyJolt genesis state.
 *
 * In TyJolt mode:
 *   1. User selects a dev account (0xAAAA, 0xBBBB, etc.)
 *   2. Transactions are signed locally (no wallet popup)
 *   3. Signed BOC is POST'd to TyJolt Producer /tx endpoint
 *   4. Producer executes TVM opcodes and returns receipt
 */

import { getNetworkConfig, TYJOLT_DEV_ACCOUNTS } from '../config/contracts';
import { submitTransactionEncrypted, isEncryptedMempoolAvailable } from './mempoolEncryption';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface TyJoltReceipt {
  tx_id: string;
  queued: boolean;
  mempool_size: number;
  receipt?: {
    tx_hash: string;
    execution_result: {
      success: boolean;
      gas_used: number;
    };
    pre_state_root: string;
    post_state_root: string;
    receipt_commitment: string;
    estimated_proof_time_ms: number;
    block_height: number;
    finality: string;
  };
}

export interface TyJoltAccountState {
  address: string;
  balance: number;
  nonce: number;
}

// ─── Dev Account Management ──────────────────────────────────────────────────

const DEV_ACCOUNT_KEY = 'whether-tyjolt-dev-account';

export function getDevAccount(): string {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem(DEV_ACCOUNT_KEY);
    if (saved) return saved;
  }
  return TYJOLT_DEV_ACCOUNTS.TRADER_1; // Default: 0xAAAA
}

export function setDevAccount(address: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(DEV_ACCOUNT_KEY, address);
  }
}

// ─── TyJolt API Calls ────────────────────────────────────────────────────────

function getProducerUrl(): string {
  const config = getNetworkConfig();
  return config.producerUrl ?? config.apiBase;
}

/**
 * Submit a transaction to TyJolt Producer.
 * Packages the BOC data into TyJolt's JSON transaction format.
 *
 * When the producer's encrypted mempool is available, transactions are
 * encrypted before submission for MEV resistance (prevents front-running
 * and sandwich attacks). Falls back to plaintext if encryption is unavailable.
 */
export async function submitTransaction(
  contractAddress: string,
  bocHex: string,
  value: number = 0,
  gasLimit: number = 100000,
): Promise<TyJoltReceipt> {
  const from = getDevAccount();
  const producerUrl = getProducerUrl();

  const txPayload = {
    from,
    to: contractAddress,
    data: bocHex.startsWith('0x') ? bocHex : '0x' + bocHex,
    value,
    gas_limit: gasLimit,
    gas_price: 1,
  };

  // Try encrypted submission first for MEV resistance
  try {
    if (await isEncryptedMempoolAvailable()) {
      const encryptedReceipt = await submitTransactionEncrypted(txPayload, from);
      if (encryptedReceipt) {
        // Encrypted submission succeeded — return a receipt-like response.
        // The tx is queued in the encrypted mempool; it will be decrypted
        // and executed when the ordering is committed and revealed.
        return {
          tx_id: encryptedReceipt.tx_hash,
          queued: true,
          mempool_size: encryptedReceipt.queue_size,
        };
      }
    }
  } catch {
    // Encrypted mempool unavailable — fall through to plaintext
  }

  // Plaintext fallback: direct submission to /tx
  const response = await fetch(`${producerUrl}/tx`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(txPayload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(`TyJolt tx failed: ${(error as any).error ?? response.statusText}`);
  }

  return await response.json() as TyJoltReceipt;
}

/**
 * Get account balance and state from TyJolt Producer.
 */
export async function getAccountState(address: string): Promise<TyJoltAccountState> {
  const producerUrl = getProducerUrl();

  try {
    const response = await fetch(`${producerUrl}/account/${address}`);
    if (response.ok) {
      return await response.json() as TyJoltAccountState;
    }
  } catch { /* offline */ }

  return { address, balance: 0, nonce: 0 };
}

/**
 * Get the current dev account's balance.
 */
export async function getDevBalance(): Promise<number> {
  const account = await getAccountState(getDevAccount());
  return account.balance;
}

/**
 * Request tokens from the TyJolt faucet.
 */
export async function requestFaucet(address?: string): Promise<boolean> {
  const config = getNetworkConfig();
  if (!config.faucetUrl) return false;

  const target = address ?? getDevAccount();

  try {
    const response = await fetch(`${config.faucetUrl}/drip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address: target }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Get TyJolt producer status.
 */
export async function getProducerStatus(): Promise<any> {
  const producerUrl = getProducerUrl();

  try {
    const response = await fetch(`${producerUrl}/status`);
    if (response.ok) return await response.json();
  } catch { /* offline */ }

  return null;
}

/**
 * Check if TyJolt producer is reachable.
 */
export async function isTyJoltOnline(): Promise<boolean> {
  const status = await getProducerStatus();
  return status !== null;
}

/**
 * Check if we're in TyJolt mode.
 */
export function isTyJoltMode(): boolean {
  const config = getNetworkConfig();
  return config.chainType === 'tyjolt';
}
